'use client';

/**
 * **The data half of an {@link InventoryBlockContext}, with one owner**
 * ([INVDRAW-17] / VALE-98).
 *
 * A per-type spec builder needs more than the record it renders: an institution
 * roster to search, a contact roster for a payee or beneficiary picker, the
 * sibling assets a liability can link to, and the specific-gift entitlement
 * shares that seed a transfer allocation. `[INVDRAW-4]` assembled all of it
 * inline in {@link InventoryAttributeDrawer}, which was right while the drawer
 * was the only surface rendering blocks.
 *
 * `[INVDRAW-17]` puts the **add dialog** on the same builders, and it needs the
 * identical assembly — so this is the *bind to the source of truth* rule (root
 * `CLAUDE.md`) applied before the second copy exists rather than after. What a
 * builder reads is decided in exactly one place; a surface supplies only the
 * half that is genuinely its own — the record, and what a commit does with a
 * patch.
 *
 * ## What it deliberately does not own
 *
 * **`item`, `onCommit`, `onInstitutionChange` and `isInstitutionEditable`.**
 * Those four are precisely where the two surfaces differ, and folding them in
 * would mean a mode flag threaded through every one of them:
 *
 * · the drawer's record is read back out of the inventory cache and its commits
 *   go straight to the server through `commitItemFields`;
 * · the add dialog's record is local buffered state and its commits go into
 *   that buffer, with a single `POST` on Save.
 *
 * The data below is the same either way, which is what makes it the seam.
 */

import { useMemo, useRef, useState } from 'react';
import { useInstitutionSearchField } from '@/lib/services/institutions';
import {
  InventoryItem,
  LIABILITY_TYPE_SLUGS
} from '@/lib/services/inventory-items/types';
import { useMatterContacts } from '@/lib/services/contacts';
import { useValueSourceDocuments } from './use-value-source-documents';
import { useEntitlementSeed } from '@/lib/services/entitlements/use-entitlement-seed';
import type { InventoryBlockContext } from './inventory-attribute-specs';

/**
 * Which asset type a liability's Linked-asset row can point at — mirrors
 * `InventoryFormCard.tsx`'s own (unexported) `LINKED_ASSET_MAP` exactly, so
 * the two surfaces never disagree about what a mortgage or a vehicle-loan
 * links to.
 */
const LINKED_ASSET_TYPE: Record<string, string> = {
  mortgage: 'real-estate',
  'vehicle-loan': 'vehicle'
};

/**
 * The types that *carry* a linked-asset row, derived from the map above rather
 * than listed a second time (VALE-1419).
 *
 * A surface rendering these blocks over a *filtered* inventory has to decide
 * whether it needs the whole matter's items, and the answer is exactly "does
 * any record here hold a linked-asset row": the Discovery drawer's Assets
 * column is scoped to one institution, and the property a mortgage points at is
 * never at the lender. Deriving it keeps that decision bound to the one
 * definition of what links to what.
 */
export const LINKED_ASSET_HOLDER_TYPES: ReadonlySet<string> = new Set(
  Object.keys(LINKED_ASSET_TYPE)
);

/**
 * The subset of {@link InventoryBlockContext} this hook supplies. Spelled as a
 * `Pick` rather than restated so a new context member can never be added here
 * with a drifted type — and so a builder reading a member this hook does not
 * yet supply is a type error at the surface, not a silent `undefined`.
 */
export type InventoryBlockData = Pick<
  InventoryBlockContext,
  | 'institutions'
  | 'loadingInstitutions'
  | 'onInstitutionSearch'
  | 'contacts'
  | 'loadingContacts'
  | 'linkedAssets'
  | 'entitlementBeneficiaryShares'
  | 'entitlementSeedLoading'
  | 'pendingPaidOutOfPocket'
  | 'onPaidOutOfPocketChange'
  | 'valueSourceDocuments'
  | 'valueSourceDocumentsPlaceholder'
  | 'matterId'
>;

export interface UseInventoryBlockDataOptions {
  /** The item's type slug, already resolved off the string-or-object union. */
  typeSlug: string;
  matterId: number;
  /**
   * The matter's inventory items, for the linked-asset options. Filtered from
   * the caller's already-loaded cache subscription rather than re-fetched here
   * — both surfaces already hold one.
   */
  items: InventoryItem[];
  /**
   * The record's id, for its specific-gift entitlement shares. **Absent while
   * adding**: an unsaved item has no entitlements pointing at it, so the
   * allocation seed is simply not available yet — the same `undefined` a
   * persisted item with no specific gift produces.
   */
  itemId?: number;
  /**
   * Identity of what is currently being edited — a changed value resets the
   * pending payee choice below. The drawer passes `open ? itemId : null` (a
   * different item, or the drawer closing); the add dialog passes the selected
   * type (a different type re-draws the whole record).
   */
  resetKey: string | number | null;
}

export function useInventoryBlockData({
  typeSlug,
  matterId,
  items,
  itemId,
  resetKey
}: UseInventoryBlockDataOptions): InventoryBlockData {
  /*
    **Institution search — plumbing a kept `component` block needs that a
    baseline row never did.** The kept-component builders
    (`inventory-attribute-specs.tsx`) construct the `<InstitutionSearchField>`
    element themselves and just need the data / callbacks handed through
    `InventoryBlockContext`. Fetch, debounce and supersession all live in the
    shared primitive (VALE-1465) — this hook only scopes it to the block's type.
  */
  const {
    institutions,
    loadingInstitutions,
    handleInstitutionSearch: onInstitutionSearch
    // Manual add form: the item type is fixed by the drawer, so restrict the picker
    // to that type's institutions (VALE-1857) — a bank-account picker lists banks only.
  } = useInstitutionSearchField({
    inventoryItemType: typeSlug,
    restrictToInventoryItemType: true,
    matterId
  });

  /*
    **Contacts — a reference-select or a kept beneficiary picker needs a
    roster a baseline row never did.** Combines [INVDRAW-7] / VALE-78's
    liability `payeeRows`, [INVDRAW-9]'s real-estate transfer-to-beneficiary
    allocation panel (mortgage's Payee row is already covered by
    `LIABILITY_TYPE_SLUGS`), [INVDRAW-10] / VALE-81's cases (the vehicle-loan
    Payee row, and `VehicleInstructionSection`'s kept beneficiary picker on
    the vehicle asset itself), and [INVDRAW-12] / VALE-83's (the kept
    `ShareInstructionSection`, mirroring `InventoryFormCard`'s own
    `isShareType` gate). Widened to `hin-shares` by [INVDRAW-13] / VALE-84 —
    its kept `HINPortfolioForm` mounts the same `ShareInstructionSection`
    internally and needs the identical roster + entitlement shares.
  */
  const needsShareContext =
    typeSlug === 'srn-shares' || typeSlug === 'hin-shares';
  /* The "other" asset's instruction panel lists recipients too (VALE-1940). */
  const needsOtherContext = typeSlug === 'other';
  const needsContacts =
    needsShareContext ||
    needsOtherContext ||
    typeSlug === 'real-estate' ||
    typeSlug === 'vehicle' ||
    LIABILITY_TYPE_SLUGS.has(typeSlug);
  const { data: contactsData, isLoading: loadingContacts } = useMatterContacts(
    matterId,
    { enabled: needsContacts }
  );
  const contacts = needsContacts ? contactsData?.contacts : undefined;

  /*
    **The payee checkbox's pending "ticked, no contact chosen yet" state**
    ([INVDRAW-7] / VALE-78, see `payeeRows` in `inventory-attribute-specs.tsx`).
    Neither surface remounts per record — the drawer has no `key` at its call
    site, and the add dialog's form view survives a Change-Type — so a pending
    choice left over from a *previous* record would otherwise leak into the
    next one. Reset it whenever `resetKey` moves.

    Tracked via a ref so this only reacts to an actual identity transition,
    not every render where a `useEffect`-driven `setState` would fire
    synchronously — the same pattern `send-follow-up-step.tsx`'s
    `lastCorrespondenceRef` uses.
  */
  const [pendingPaidOutOfPocket, setPendingPaidOutOfPocket] = useState<
    boolean | undefined
  >(undefined);

  const resetKeyRef = useRef(resetKey);
  /* eslint-disable react-hooks/refs -- React's documented "adjust state when props change" shape; the comment above records why a useEffect here would fire on every render instead of on the identity transition. */
  if (resetKeyRef.current !== resetKey) {
    resetKeyRef.current = resetKey;
    if (pendingPaidOutOfPocket !== undefined) {
      setPendingPaidOutOfPocket(undefined);
    }
  }
  /* eslint-enable react-hooks/refs */

  /*
    **Linked-asset options — filtered from the already-loaded inventory
    cache, no extra fetch.** Mirrors `InventoryFormCard.tsx`'s own
    `linkedAssets` derivation for `LinkedAssetField` — `[INVDRAW-9]`'s
    mortgage → real-estate, generalised by [INVDRAW-10] / VALE-81 to also
    cover vehicle-loan → vehicle via the shared `LINKED_ASSET_TYPE` map.
  */
  const linkedAssetType = LINKED_ASSET_TYPE[typeSlug] ?? null;
  const linkedAssets = useMemo(() => {
    if (!linkedAssetType) return undefined;
    return items
      .filter((candidate) => {
        const slug =
          typeof candidate.type === 'string'
            ? candidate.type
            : candidate.type?.slug;
        return slug === linkedAssetType;
      })
      .map((candidate) => ({
        id: candidate.id,
        description: candidate.description || `Item #${candidate.id}`,
        value: candidate.value ?? null
      }));
  }, [items, linkedAssetType]);

  /*
    **The seed's inputs, and whether they have settled** — see
    `useEntitlementSeed`, which owns both the resolution and the VALE-817 rule
    that an unloaded fetch is indistinguishable from a genuine absence. This
    hook and `InventoryFormCard` each used to restate that plumbing, and four
    further surfaces needed it (VALE-1431), so it lives in one place now.
  */
  const { sharesFor, seedLoading: entitlementSeedLoading } = useEntitlementSeed(
    {
      matterId,
      enabled: needsShareContext || needsOtherContext,
      contactsLoading: loadingContacts
    }
  );
  const entitlementBeneficiaryShares = sharesFor(itemId);

  /*
    **What a value confirmation can be cited against** (VALE-355) — the matter's
    own documents, plus the attachments on the item institution's replies as their
    own labelled group (LF-5393, the same split
    `shared/value-source-select-content.tsx` renders on the flag-off form).

    **Only for a persisted record.** `itemId == null` is the add dialog, where a
    create request has no confirmation member to carry — `confirmationRow` hides
    itself there — so neither query is worth firing.

    The placeholder is the picker's whole empty state, and it has to tell three
    situations apart: still loading, the fetch failed, and a matter that genuinely
    has no documents. Collapsing the middle one into the last is exactly the defect
    LF-6314 fixed on the flag-off picker ("Link a document (optional)" over an
    empty list reads as "this matter has no documents"), so it is spelled out here
    rather than left to the editor to guess.
  */
  /*
    **What a figure can be cited against — bound, not restated** (VALE-1032).
    `useValueSourceDocuments` owns the citation order, the way an institution's
    reply names whose reply it is, and the three-way placeholder, because the
    inventory list now needs exactly this and none of the rest of this hook.
    Unconditional here: the drawer and the add dialog each hold one open record.
  */
  const { valueSourceDocuments, valueSourceDocumentsPlaceholder } =
    useValueSourceDocuments({ matterId, itemId });

  return {
    institutions,
    loadingInstitutions,
    onInstitutionSearch,
    contacts,
    loadingContacts,
    linkedAssets,
    entitlementBeneficiaryShares,
    entitlementSeedLoading,
    pendingPaidOutOfPocket,
    onPaidOutOfPocketChange: setPendingPaidOutOfPocket,
    valueSourceDocuments,
    valueSourceDocumentsPlaceholder,
    matterId
  };
}
